import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ClipboardList, Target, BarChart3, TrendingUp, Users, Shield, LogOut, Settings, HelpCircle, ChevronLeft, ChevronRight, LayoutDashboard, Activity, UsersRound, ScrollText, CreditCard, BookOpen, Bell, Unlock, Cog, FileText, LayoutGrid, ArrowLeftRight, Calculator } from 'lucide-react';
import { toast } from 'sonner';

const mainNavItems = [
  { path: '/agent/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/agent/dashboard', label: 'Weekly Activity', icon: ClipboardList, hash: '#activity' },
  { path: '/agent/goals', label: 'Weekly Goals', icon: Target },
  { path: '/agent/daily-activity', label: 'Daily Activity', icon: FileText },
  { path: '/agent/reports', label: 'Reports', icon: BarChart3 },
  { path: '/agent/performance', label: 'Performance', icon: TrendingUp },
  { path: '/agent/leads', label: 'Leads', icon: Users },
  { path: '/agent/quote-tool', label: 'Quoting Tool', icon: Calculator },
  { path: '/agent/training', label: 'Training', icon: BookOpen },
  { path: '/agent/billing', label: 'Billing', icon: CreditCard },
];

const adminNavItems = [
  { path: '/admin', label: 'Overview', icon: Shield },
  { path: '/admin/users', label: 'User Management', icon: UsersRound },
  { path: '/admin/leads', label: 'Leads', icon: Users },
  { path: '/admin/quote-tool', label: 'Quoting Tool', icon: Calculator },
  { path: '/admin/teams', label: 'Teams', icon: Users },
  { path: '/admin/metrics', label: 'Metrics', icon: Activity },
  { path: '/admin/audit-log', label: 'Audit Log', icon: ScrollText },
  { path: '/admin/notifications', label: 'Notifications', icon: Bell },
  { path: '/admin/unlock-requests', label: 'Unlock Requests', icon: Unlock },
  { path: '/admin/settings', label: 'Settings', icon: Cog },
];

const AgentSidebar = ({ collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const agentInfo = JSON.parse(localStorage.getItem('agentInfo') || '{}');
  const isAdmin = agentInfo?.role === 'admin' || agentInfo?.role === 'super_admin';
  const isAdminArea = location.pathname.startsWith('/admin');

  const handleLogout = () => {
    localStorage.removeItem('agentToken');
    localStorage.removeItem('agentInfo');
    toast.success('Logged out successfully');
    navigate('/agent/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-slate-900 text-white z-40 flex flex-col transition-all duration-300 print:hidden ${
        collapsed ? 'w-[68px]' : 'w-60'
      }`}
      data-testid="agent-sidebar"
    >
      {/* Logo / Brand */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
        <div className="w-9 h-9 bg-brand-blue rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-sm">
          PC
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <div className="text-sm font-bold leading-tight truncate">Purposefully Covered</div>
            <div className="text-[10px] text-white/50">{isAdminArea ? 'Admin Dashboard' : 'Agent Portal'}</div>
          </div>
        )}
      </div>

      {/* Agent Info */}
      {!collapsed && (
        <div className="px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-brand-orange flex items-center justify-center text-xs font-bold flex-shrink-0">
              {agentInfo?.fullName?.split(' ').map(n => n[0]).join('').slice(0,2)}
            </div>
            <div className="overflow-hidden">
              <div className="text-sm font-semibold truncate">{agentInfo?.fullName}</div>
              <div className="text-[10px] text-white/50 truncate">{agentInfo?.email}</div>
            </div>
          </div>
        </div>
      )}
      {collapsed && (
        <div className="flex justify-center py-3 border-b border-white/10">
          <div className="w-8 h-8 rounded-full bg-brand-orange flex items-center justify-center text-xs font-bold">
            {agentInfo?.fullName?.split(' ').map(n => n[0]).join('').slice(0,2)}
          </div>
        </div>
      )}

      {/* Main Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto">
        <div className={`${collapsed ? '' : 'px-3'}`}>
          {/* Admin-only switcher */}
          {isAdmin && (
            <>
              <button
                onClick={() => navigate('/portal')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] rounded-lg mb-0.5 text-sm text-white/70 hover:bg-white/10 hover:text-white transition ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? 'Portal Home' : undefined}
                data-testid="sidebar-portal-home"
              >
                <LayoutGrid size={18} className="flex-shrink-0" />
                {!collapsed && <span>Portal Home</span>}
              </button>
              <button
                onClick={() => navigate(isAdminArea ? '/agent/dashboard' : '/admin')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] rounded-lg mb-0.5 text-sm text-brand-orange hover:bg-brand-orange/10 transition ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? (isAdminArea ? 'Switch to Agent Portal' : 'Switch to Admin Dashboard') : undefined}
                data-testid="sidebar-switch-area"
              >
                <ArrowLeftRight size={18} className="flex-shrink-0" />
                {!collapsed && <span>{isAdminArea ? 'Agent Portal' : 'Admin Dashboard'}</span>}
              </button>
              {!collapsed && <div className="my-3 border-t border-white/10"></div>}
              {collapsed && <div className="my-2 border-t border-white/10"></div>}
            </>
          )}

          {!collapsed && <div className="text-[10px] uppercase tracking-wider text-white/30 font-semibold px-3 mb-2">{isAdminArea ? 'Admin' : 'Navigation'}</div>}

          {isAdminArea ? (
            adminNavItems.map(({ path, label, icon: Icon }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] rounded-lg mb-0.5 text-sm transition ${
                  isActive(path) ? 'bg-brand-blue text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                } ${collapsed ? 'justify-center' : ''}`}
                title={collapsed ? label : undefined}
                data-testid={`sidebar-admin-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon size={18} className="flex-shrink-0" />
                {!collapsed && <span>{label}</span>}
              </button>
            ))
          ) : (
            mainNavItems.map(({ path, label, icon: Icon }) => {
              const active = isActive(path) && label !== 'Dashboard';
              const isDashboard = label === 'Dashboard';
              const dashActive = isDashboard && isActive('/agent/dashboard');
              return (
                <button
                  key={label}
                  onClick={() => navigate(path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] rounded-lg mb-0.5 text-sm transition group ${
                    (active || dashActive)
                      ? 'bg-brand-blue text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  } ${collapsed ? 'justify-center' : ''}`}
                  title={collapsed ? label : undefined}
                  data-testid={`sidebar-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon size={18} className="flex-shrink-0" />
                  {!collapsed && <span className="truncate">{label}</span>}
                </button>
              );
            })
          )}
        </div>
      </nav>

      {/* Bottom Section */}
      <div className={`border-t border-white/10 py-3 ${collapsed ? '' : 'px-3'}`}>
        <button
          onClick={() => navigate('/agent/settings')}
          className={`w-full flex items-center gap-3 px-3 py-2 min-h-[44px] rounded-lg text-sm transition ${
            isActive('/agent/settings')
              ? 'bg-brand-blue text-white'
              : 'text-white/50 hover:bg-white/10 hover:text-white'
          } ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Settings' : undefined}
          data-testid="sidebar-settings"
        >
          <Settings size={18} className="flex-shrink-0" />
          {!collapsed && <span>Settings</span>}
        </button>
        <button
          onClick={() => navigate('/faq')}
          className={`w-full flex items-center gap-3 px-3 py-2 min-h-[44px] rounded-lg text-sm text-white/50 hover:bg-white/10 hover:text-white transition ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Help' : undefined}
          data-testid="sidebar-help"
        >
          <HelpCircle size={18} className="flex-shrink-0" />
          {!collapsed && <span>Help & FAQ</span>}
        </button>
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-3 py-2 min-h-[44px] rounded-lg text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Logout' : undefined}
          data-testid="sidebar-logout"
        >
          <LogOut size={18} className="flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>

      {/* Collapse Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center hover:bg-slate-600 transition z-50"
        data-testid="sidebar-collapse-btn"
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
};

export default AgentSidebar;
